import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getHistory, removeHistoryEntry, clearHistory, HistoryEntry } from '../../utils/historyStorage';
import StatisticsView from '../StatisticsView/StatisticsView';
import { useOptimization } from '../../context/OptimizationContext';
import ComparisonView from '../ComparisonView/ComparisonView';

interface HistoryViewProps {
  onSelectResult?: (entry: HistoryEntry) => void;
}

const HistoryView: React.FC<HistoryViewProps> = ({ onSelectResult }) => {
  const { results, isRunning, showNotification } = useOptimization();
  const [history, setHistory] = useState<HistoryEntry[]>(getHistory());
  const [selectedEntry, setSelectedEntry] = useState<HistoryEntry | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<string | null>(null);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [clearAllConfirmOpen, setClearAllConfirmOpen] = useState(false);

  const refreshHistory = () => {
    setHistory(getHistory());
  };

  // Refresh history on mount, when results change, and when optimization completes
  useEffect(() => {
    refreshHistory();
  }, [results]); // Refresh when results change

  // Also refresh when optimization stops (completes)
  useEffect(() => {
    if (!isRunning && results) {
      // Small delay to ensure localStorage has been updated
      setTimeout(() => {
        refreshHistory();
      }, 500);
    }
  }, [isRunning, results]);

  const handleView = (entry: HistoryEntry) => {
    setSelectedEntry(entry);
    setViewDialogOpen(true);
    if (onSelectResult) {
      onSelectResult(entry);
    }
  };

  const handleDelete = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setEntryToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (entryToDelete) {
      removeHistoryEntry(entryToDelete);
      refreshHistory();
      setEntryToDelete(null);
    }
    setDeleteConfirmOpen(false);
  };

  const handleClearAll = () => {
    setClearAllConfirmOpen(true);
  };

  const confirmClearAll = () => {
    clearHistory();
    refreshHistory();
    setClearAllConfirmOpen(false);
    showNotification('All history cleared successfully', 'success');
  };

  const sortedHistory = useMemo(() => {
    return [...history].sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [history]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h2">
            Results History
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<CompareArrowsIcon />}
            onClick={() => setComparisonOpen(true)}
            disabled={history.length < 2}
            sx={{ mr: 1 }}
          >
            Compare
          </Button>
          <IconButton onClick={refreshHistory} size="small" sx={{ mr: 1 }} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleClearAll}
            disabled={history.length === 0}
          >
            Clear All
          </Button>
        </Box>
      </Box>

      {sortedHistory.length === 0 ? (
        <Alert severity="info">
          No results history available. Run optimizations to see them here.
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Timestamp</strong></TableCell>
                <TableCell align="right"><strong>Tasks</strong></TableCell>
                <TableCell align="right"><strong>Population</strong></TableCell>
                <TableCell align="right"><strong>Runs</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedHistory.map((entry) => (
                <TableRow
                  key={entry.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleView(entry)}
                >
                  <TableCell>
                    {new Date(entry.timestamp).toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    {entry.results.configuration.taskCount}
                  </TableCell>
                  <TableCell align="right">
                    {entry.results.configuration.initialPopulation}
                  </TableCell>
                  <TableCell align="right">
                    {entry.results.runNumber ? (
                      <Chip label={entry.results.runNumber} size="small" color="primary" />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => handleDelete(entry.id, e)}
                      title="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Results Details - {selectedEntry && new Date(selectedEntry.timestamp).toLocaleString()}
        </DialogTitle>
        <DialogContent>
          {selectedEntry && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Configuration:</strong> Tasks={selectedEntry.results.configuration.taskCount}, 
                  Properties={selectedEntry.results.configuration.propertyCount}, 
                  Population={selectedEntry.results.configuration.initialPopulation}
                  {selectedEntry.results.runNumber && (
                    <> • <strong>Runs:</strong> {selectedEntry.results.runNumber}</>
                  )}
                </Typography>
              </Alert>
              {selectedEntry.results.statistics ? (
                <StatisticsView results={selectedEntry.results} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No statistics available for this result.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Result</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this result from history?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Confirmation Dialog */}
      <Dialog open={clearAllConfirmOpen} onClose={() => setClearAllConfirmOpen(false)}>
        <DialogTitle>Clear All History</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
              Are you sure you want to clear all history?
            </Typography>
            <Typography variant="body2">
              This action cannot be undone. All {history.length} result{history.length !== 1 ? 's' : ''} will be permanently deleted.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearAllConfirmOpen(false)}>
            Cancel
          </Button>
          <Button onClick={confirmClearAll} color="error" variant="contained">
            Clear All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comparison Dialog */}
      <ComparisonView 
        open={comparisonOpen} 
        onClose={() => setComparisonOpen(false)}
        onError={(message) => showNotification(message, 'error')}
      />
    </Box>
  );
};

export default HistoryView;

